import React, { useState } from 'react';
import styled from 'styled-components';
import { DownloadIcon, UploadIcon } from 'lucide-react';
import {
  FormActions,
  FormCancelButton,
  FormSubmitButton,
} from '../../Components/Form';
import apiClient from '../../Services/ApiClient';
import apiClientFormData from '../../Services/ApiClientFormData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import { COLORS } from '../../Styles/Colors';
import { BIN_FIELD_LIMITS } from './binLocationName';

const ImportStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 8px;
  background-color: #f8f9fa;
`;

const ImportStepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitle = styled.span`
  color: ${COLORS.darkGray};
  font-size: 1rem;
  font-weight: 600;
`;

const ImportInstructionText = styled.p`
  margin: 0;
  color: ${COLORS.darkGray};
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
`;

const ColumnList = styled.ul`
  margin: 0;
  padding: 12px 16px 12px 32px;
  background-color: ${COLORS.white};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.orange};
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  line-height: 1.8;
`;

const TemplateDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-2px);
  }
`;

const FileDropZone = styled.label<{ $hasFile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 20px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? COLORS.teal : COLORS.gray)};
  border-radius: 8px;
  background-color: ${COLORS.white};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.teal};
    background-color: ${COLORS.teal}08;
  }

  input {
    display: none;
  }
`;

const SelectedFileName = styled.span`
  color: ${COLORS.teal};
  font-size: 0.9rem;
  font-weight: 600;
  word-break: break-all;
  text-align: center;
`;

const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImportContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 560px;
`;

const ImageUploadText = styled.span`
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const ImageUploadSubtext = styled.span`
  color: ${COLORS.lightGray};
  font-size: 0.75rem;
  text-align: center;
`;

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const BinBulkImport: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient('/api/bins/download-template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Bin-Import-Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!file) {
      showSnackbar('Please select a file', 'error');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClientFormData('/api/bins/import', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Import failed');
      }
      const result = await response.json();
      const msg = result.errors?.length
        ? `Imported ${result.imported} bin${result.imported === 1 ? '' : 's'}. ${result.errors.length} row(s) had errors.`
        : `Successfully imported ${result.imported} bin${result.imported === 1 ? '' : 's'}`;
      showSnackbar(msg, result.errors?.length ? 'error' : 'success');
      if (result.imported > 0) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <ImportWrapper>
      <ImportContent>
        <ImportInstructionText>
          Import multiple bins at once using an Excel spreadsheet.
          Download the template first to ensure your file has the correct column format.
        </ImportInstructionText>

        <ColumnList>
          <li><strong>Location</strong> — select from dropdown (OVID-Name); bin-enabled locations only</li>
          <li><strong>Aisle</strong> — required, max {BIN_FIELD_LIMITS.ailse} characters</li>
          <li><strong>Rack / Shelf / Bin</strong> — optional, max {BIN_FIELD_LIMITS.rack} characters each</li>
          <li>Bin location name is generated automatically as Aisle-Rack-Shelf-Bin</li>
        </ColumnList>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>1</StepNumber>
            <StepTitle>Download the template</StepTitle>
          </ImportStepHeader>
          <TemplateDownloadButton type="button" onClick={handleDownloadTemplate}>
            <DownloadIcon size={18} />
            Download Excel Template
          </TemplateDownloadButton>
        </ImportStep>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>2</StepNumber>
            <StepTitle>Upload your completed file</StepTitle>
          </ImportStepHeader>
          <FileDropZone $hasFile={!!file}>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <UploadIcon size={28} color={COLORS.teal} />
            {file ? (
              <SelectedFileName>{file.name}</SelectedFileName>
            ) : (
              <>
                <ImageUploadText>Click to select an .xlsx file</ImageUploadText>
                <ImageUploadSubtext>Only Excel (.xlsx) files are accepted</ImageUploadSubtext>
              </>
            )}
          </FileDropZone>
        </ImportStep>

        <FormActions onCancel={onClose}>
          <FormCancelButton type="button" onClick={onClose}>Cancel</FormCancelButton>
          <FormSubmitButton type="button" onClick={handleImport} disabled={!file || importing}>
            {importing ? 'Importing...' : 'Import Bins'}
          </FormSubmitButton>
        </FormActions>
      </ImportContent>
    </ImportWrapper>
  );
};

export default BinBulkImport;
